import { useState } from "react";

import { clearStorage } from "../utils/storageUtils";
import { useMsal } from "@azure/msal-react";
import { LogOut, RefreshCw, Menu, X, Camera, PanelRightIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { setMonitoringMode } from "../redux/slices/appSlice";
import { useDispatch, useSelector } from 'react-redux';

function Header({
    isRefreshing,
    onRefresh,
    title = "Live Production Images"
}) {

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const navigate = useNavigate();

    // redux
    const dispatch = useDispatch();
    const mode = useSelector(state => state.app.monitoringMode);

    function onClickLogOut() {
        if (!activeAccount) {
            navigate("/");
            return
        }

        clearStorage(activeAccount);

        instance.logoutPopup({
            postLogoutRedirectUri: "/",
            mainWindowRedirectUri: "/"
        })
    }

    const closeMenu = () => setIsMenuOpen(false);

    return (
        <div className="relative">
            <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-20">
                <div className="px-4 py-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            {/* Menu Button */}
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                            </button>

                            <div className="flex items-center space-x-2">
                                <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
                                <span className={`inline-block mt-1 text-xs ${mode == 'live' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'} px-2 py-1 rounded-full`}>
                                    {mode == 'live' ? 'Live' : 'Timeline'}
                                </span>
                            </div>
                        </div>

                        <div className="flex items-center space-x-2">
                            <button
                                onClick={onRefresh}
                                disabled={isRefreshing}
                                className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                            </button>
                            <button
                                onClick={onClickLogOut}
                                className="p-2 text-gray-600 hover:text-danger-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <LogOut className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {/* User Info */}
                    <div className="mt-2 text-xs text-gray-500">
                        Welcome, {activeAccount?.name}
                    </div>
                </div>
            </header>

            {/* Navigation Dropdown Menu */}
            {isMenuOpen && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black bg-opacity-25 z-10"
                        onClick={closeMenu}
                    />

                    {/* Menu Content */}
                    <div className="absolute top-full left-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 z-30 max-w-md mt-2">
                        <div className="p-4">
                            <h3 className="text-sm font-medium text-gray-700 mb-3">Navigation</h3>
                            <div className="space-y-2">

                                <div
                                    onClick={() => dispatch(setMonitoringMode({ monitoringMode: 'live' }))}
                                    className={`block p-3 rounded-lg border transition-colors ${mode == 'live'
                                        ? 'bg-blue-50 border-blue-200'
                                        : 'border-gray-200 hover:bg-gray-50'
                                        }`}
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className={`p-2 rounded-lg ${mode == 'live'
                                            ? 'bg-blue-100'
                                            : 'bg-gray-100'
                                            }`}>
                                            <Camera className={`h-5 w-5 ${mode == 'live'
                                                ? 'text-blue-600'
                                                : 'text-gray-600'
                                                }`} />
                                        </div>
                                        <div className="flex-1">
                                            <h4 className={`text-sm font-medium ${mode == 'live'
                                                ? 'text-blue-900'
                                                : 'text-gray-900'
                                                }`}>
                                                Live Images
                                            </h4>
                                            <p className={`text-xs ${mode == 'live'
                                                ? 'text-blue-700'
                                                : 'text-gray-600'
                                                }`}>
                                                View real-time images
                                            </p>
                                            {mode == 'live' && (
                                                <span className="inline-block mt-1 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                                    Current
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div
                                    onClick={() => dispatch(setMonitoringMode({ monitoringMode: 'timeline' }))}
                                    className={`block p-3 rounded-lg border transition-colors ${mode == 'timeline'
                                        ? 'bg-green-50 border-green-200'
                                        : 'border-gray-200 hover:bg-gray-50'
                                        }`}
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className={`p-2 rounded-lg ${mode == 'timeline'
                                            ? 'bg-green-100'
                                            : 'bg-gray-100'
                                            }`}>
                                            <PanelRightIcon className={`h-5 w-5 ${mode == 'timeline'
                                                ? 'text-green-600'
                                                : 'text-gray-600'
                                                }`} />
                                        </div>
                                        <div className="flex-1">
                                            <h4 className={`text-sm font-medium ${mode == 'timeline'
                                                ? 'text-green-900'
                                                : 'text-gray-900'
                                                }`}>
                                                Timeline Images
                                            </h4>
                                            <p className={`text-xs ${mode == 'timeline'
                                                ? 'text-green-700'
                                                : 'text-gray-600'
                                                }`}>
                                                View timeline images
                                            </p>
                                            {mode == 'timeline' && (
                                                <span className="inline-block mt-1 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                                    Current
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Header